import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  FileText,
  Eye,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

import { API_BASE_URL } from '../config/api';
import authService from '../services/authService';

// Add API function to fetch projects
const fetchProjects = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/projects`);
    if (!response.ok) {
      throw new Error('Failed to fetch projects');
    }
    const data = await response.json();
    return data.projects || [];
  } catch (error) {
    return [];
  }
};

const ResearchLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('30');
  const [loadingProjectId, setLoadingProjectId] = useState<number | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // Get current user
  const currentUser = authService.getCurrentUser();
  const displayName = currentUser?.first_name 
    ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
    : currentUser?.username || 'User';

  // Fetch projects on component mount
  useEffect(() => {
    const loadProjects = async () => {
      setIsLoading(true);
      const projectList = await fetchProjects();
      setProjects(projectList);
      setFilteredProjects(projectList);
      setIsLoading(false);
    };

    loadProjects();
  }, []);

  // Filter projects based on search and status
  useEffect(() => {
    let filtered = projects;
    
    if (searchTerm) {
      filtered = filtered.filter(project => 
        project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.brand_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.product_info?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }
    
    setFilteredProjects(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [projects, searchTerm, statusFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleStartResearch = async (projectId: number) => {
    try {
      setLoadingProjectId(projectId);
      
      // Use the new endpoint to ensure research reports exist
      const response = await fetch(`${API_BASE_URL}/api/ensure-research-reports/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        await response.json();
        // Navigate to dashboard regardless of the result
        navigate(`/research-agent-dashboard/${projectId}`);
    } else {
        // Still navigate to dashboard even if the check fails
        navigate(`/research-agent-dashboard/${projectId}`);
      }
    } catch (error) {
      // Navigate to dashboard even if there's an error
      navigate(`/research-agent-dashboard/${projectId}`);
    } finally {
      setLoadingProjectId(null);
    }
  };

  const getProjectStatus = () => {
    // Mock status based on project data - you can replace with actual status logic
    const statuses = ['Completed', 'In Progress', 'Draft', 'Failed'];
    const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
    return randomStatus;
  };





  const getProjectImage = (project: any) => {
    const isAbsolute = (u: string) => /^https?:\/\//i.test(u);
    const buildUrl = (u: string) => (isAbsolute(u) ? u : `${API_BASE_URL}${u}`);

    // Prefer explicit URL provided by backend
    if (project.primary_image_url) {
      return buildUrl(project.primary_image_url);
    }
    // Fallbacks to structured image object if present
    if (project.primary_image?.image_url) {
      return buildUrl(project.primary_image.image_url);
    }
    if (project.primary_image?.original_url) {
      return buildUrl(project.primary_image.original_url);
    }
    // No image available — show neutral placeholder
    return 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50"><rect width="50" height="50" fill="#F0F0F0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="10" fill="#9CA3AF">No Image</text></svg>');
  };

  const stats = {
    inProgress: projects.filter(() => getProjectStatus() === 'In Progress').length,
    completed: projects.filter(() => getProjectStatus() === 'Completed').length,
    failed: projects.filter(() => getProjectStatus() === 'Failed').length,
    draft: projects.filter(() => getProjectStatus() === 'Draft').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div 
          className="flex justify-between items-center mb-8 p-6"
          style={{
            borderRadius: '20px',
            border: '1px solid #F8F9FA',
            background: '#FFF',
            boxShadow: '0 4px 20px 0 rgba(238, 238, 238, 0.50)'
          }}
        >
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hello {displayName} 👋</h2>
            <p className="text-gray-600">Let's learn something new today!</p>
          </div>
          <button 
            className="text-white hover:opacity-90 transition-opacity"
            style={{
              borderRadius: '12px',
              border: '1px solid #FFF',
              background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)',
              display: 'flex',
              padding: '16px 12px',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <div 
              className="flex items-center justify-center"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#fff'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 3C12.5523 3 13 3.44772 13 4V11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H13V20C13 20.5523 12.5523 21 12 21C11.4477 21 11 20.5523 11 20V13H4C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11H11V4C11 3.44772 11.4477 3 12 3Z" fill="#413692"/>
                              </svg>
                            </div>
            <span>Create New Project</span>
          </button>
        </div>

        {/* Overview Section */}
        <div 
          className="p-6 mb-8"
          style={{
            borderRadius: '20px',
            border: '1px solid #F8F9FA',
            background: '#FFF',
            boxShadow: '0 4px 20px 0 rgba(238, 238, 238, 0.50)'
          }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
            <div className="flex items-center space-x-2">
              <select 
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
                        </div>
                      </div>
          
          <div className="flex flex-row gap-8">
            <div 
              className="p-4 text-white flex-1"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
            >
              <div className="flex flex-col items-left justify-between h-full">
              <Clock className="w-8 h-8 opacity-80" style={{ marginBottom: '10px' }} />
                <div className="text-left mb-4">
                  <p className="text-sm opacity-90">Projects In Progress</p>
                  <p className="text-2xl font-bold">{stats.inProgress}</p>
                </div>
               
              </div>
            </div>
            
            <div 
              className="p-4 text-white flex-1"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
            >
              <div className="flex flex-col items-left justify-between h-full">
              <CheckCircle className="w-8 h-8 opacity-80" style={{ marginBottom: '10px' }} />
                <div className="text-left mb-4">
                  <p className="text-sm opacity-90">Projects Completed</p>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                </div>
                
              </div>
            </div>
            
            <div 
              className="p-4 text-white flex-1"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
            >
              <div className="flex flex-col items-left justify-between h-full">
                <FileText className="w-8 h-8 opacity-80" style={{ marginBottom: '10px' }} />
                <div className="text-left mb-4">
                  <p className="text-sm opacity-90">Projects Draft</p>
                  <p className="text-2xl font-bold">{stats.draft}</p>
                  </div>
              
              </div>
            </div>
            
            <div 
              className="p-4 text-white flex-1"
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}
            >
              <div className="flex flex-col items-left justify-between h-full">
              <XCircle className="w-8 h-8 opacity-80" style={{ marginBottom: '10px' }} />
                <div className="text-left mb-4">
                  <p className="text-sm opacity-90">Projects Failed</p>
                  <p className="text-2xl font-bold">{stats.failed}</p>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex justify-end mb-6">
          <div className="flex flex-row gap-4 w-1/2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search for anything..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{
                  borderRadius: '12px',
                  border: '1px solid #ECECEC',
                  background: '#FFF',
                  boxShadow: '0 4px 6px 0 rgba(0, 0, 0, 0.02)'
                }}
              />
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="focus:outline-none focus:ring-2 focus:ring-purple-500 px-3 py-2"
                style={{
                  borderRadius: '12px',
                  border: '1px solid #ECECEC',
                  background: '#FFF'
                }}
              >
                <option value="all">Status</option>
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Draft">Draft</option>
                <option value="Failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Project List Table */}
        <div 
          className="overflow-hidden"
          style={{
            borderRadius: '12px',
            border: '1px solid #ECECEC'
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead 
                style={{
                  borderRadius: '12px 12px 0 0',
                  borderBottom: '1px solid #ECECEC',
                  background: '#F0ECFF'
                }}
              >
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ASIN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      Loading projects...
                    </td>
                  </tr>
                ) : filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No projects found
                    </td>
                  </tr>
                ) : (
                  currentProjects.map((project) => {
                    const status = 'Completed';
                    const progress = 100;
                    return (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              className="w-10 h-10 rounded-lg object-cover mr-3"
                              src={getProjectImage(project)}
                              alt={project.project_name}
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {project.project_name || 'Premium Water Bottle Campaign'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {project.brand_name || 'Brand Owner'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {project.amazon_listing_link ? 
                            project.amazon_listing_link.split('/').pop()?.substring(0, 10) || 'BOXYZABC1' : 
                            'BOXYZABC1'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-900">{progress}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(project.created_at || Date.now()).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {project.brand_name ? 'Brand Owner' : 'Agency Owner'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleStartResearch(project.id)}
                              disabled={loadingProjectId === project.id}
                              className={`text-xs ${
                                loadingProjectId === project.id 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-purple-600 hover:text-purple-900'
                              }`}
                            >
                              {loadingProjectId === project.id ? 'Creating...' : 'Send for Research'}
                            </button>
                            <button className="text-gray-400 hover:text-gray-600">
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Info and Controls */}
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects
          </div>
          
          {totalPages > 1 && (
            <div className="flex justify-center">
            <nav className="flex items-center space-x-2">
              <button 
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm rounded-md ${
                  currentPage === 1 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                &lt;
              </button>
              
              {/* Show page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current page
                const shouldShow = 
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1);
                
                if (!shouldShow) {
                  // Show ellipsis for gaps
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="px-3 py-2 text-sm text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm rounded-md ${
                      currentPage === page
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button 
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 text-sm rounded-md ${
                  currentPage === totalPages 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                &gt;
              </button>
            </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResearchLandingPage; 